// @owners { team: patients }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  Body,
  Button,
  Card,
  Column,
  InputCheckbox,
  LgPadding,
  LoadingEmptyState,
  MdPadding,
  SecondaryPage,
  SizedImage,
  Switch,
  XlPadding,
  useScreenSize,
} from '@alto/design-system';
import { useQueryPhiAuthorizationConfigs } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React, { useEffect, useMemo } from 'react';
import { useController, useForm } from 'react-hook-form';
import styledNative from 'styled-components/native';
// eslint-disable-next-line import/no-deprecated
import { updateOnboardingUser } from '~shared/actions/onboarding';
import {
  ONBOARD_STATE_PHI_AUTHORIZATION,
  PHI_AUTHORIZATION,
} from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { routeFromPath } from '~shared/features/onboarding/helpers/routeToPath';
import getLoading from '~shared/features/ui/selectors/getLoading';
import getShowProgynySelfServiceForUser from '~shared/features/users/selectors/getShowProgynySelfServiceForUser';
import { getUserPhiAuth } from '~shared/features/users/selectors/getUserPhiAuth';
import { useAnalytics, useIsFeatureDisabledForCompany, usePageViewedEvent } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { useGetInsurances } from '~shared/queries/query-keys/insurances';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { PhiAuthCommsPreferencesText, PhiAuthCommsPreferencesTextWithoutPush } from '~web/constants';
import { OnboardingHeader } from '~web/features/onboarding/components/redesign/OnboardingHeader';
import { OnboardingNavBarLogo } from '~web/features/onboarding/components/redesign/OnboardingNavBarLogo';
import { nextUrl as getNextUrl, previousUrl as getPreviousUrl } from '~web/features/onboarding/helpers/urlHandler';
import onboardingPhiImage from '~web/images/onboarding_phi.png';

const StyledSizedImage = styledNative(SizedImage)`
  width: 100%;
`;

type Values = {
  sms_authorized: boolean;
  email_authorized: boolean;
  push_authorized: boolean;
};

const OnboardingPhiAuthorization = () => {
  const { navigate } = useNavigation();
  const dispatch = useDispatchShared();
  const { isLGScreenOrBigger } = useScreenSize();
  const { trackEvent } = useAnalytics();
  const showProgyny = useSelectorShared(getShowProgynySelfServiceForUser);
  const { hasInsurance } = useGetInsurances();
  const previousUrl = getPreviousUrl({ currentRoute: PHI_AUTHORIZATION });
  const nextRoute = getNextUrl({ params: { hasInsurance }, currentRoute: PHI_AUTHORIZATION });
  const { updateOnboardingUserLoading } = useSelectorShared(getLoading);
  const origin = previousUrl ? routeFromPath(previousUrl) : '';
  const userPhiAuth = useSelectorShared(getUserPhiAuth);
  const title = 'Health information preferences';
  const { isFeatureDisabled: shouldDisablePushNotification, isLoading: isFeatureDisablingLoading } =
    useIsFeatureDisabledForCompany('push_notification');

  const subTitle =
    shouldDisablePushNotification || isFeatureDisablingLoading
      ? PhiAuthCommsPreferencesTextWithoutPush
      : PhiAuthCommsPreferencesText;
  const { headerBackgroundColor, headerLogoUrl, isFetching, partnershipName } = useQueryPhiAuthorizationConfigs();

  const defaultValues = useMemo<Values>(
    () =>
      userPhiAuth?.user_prompted
        ? {
            sms_authorized: userPhiAuth.sms_authorized ?? true,
            email_authorized: userPhiAuth.email_authorized ?? true,
            push_authorized: shouldDisablePushNotification ? false : (userPhiAuth.push_authorized ?? true),
          }
        : {
            sms_authorized: true,
            email_authorized: true,
            push_authorized: shouldDisablePushNotification ? false : true,
          },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userPhiAuth?.user_prompted, shouldDisablePushNotification],
  );

  const { control, handleSubmit, setValue, watch } = useForm<Values>({
    defaultValues,
    mode: 'onSubmit',
  });

  const smsController = useController({ control, name: 'sms_authorized' });
  const emailController = useController({ control, name: 'email_authorized' });
  const pushController = useController({ control, name: 'push_authorized' });

  const smsValue = watch('sms_authorized');
  const emailValue = watch('email_authorized');
  const pushValue = watch('push_authorized');

  const allPhiAuthed = smsValue && emailValue && (shouldDisablePushNotification ? true : pushValue);

  const onBack = () => {
    if (previousUrl) {
      navigate(previousUrl);
    }
  };

  const onSubmit = ({ sms_authorized, email_authorized, push_authorized }: Values) => {
    const phi_auth_data = {
      sms_authorized,
      email_authorized,
      push_authorized: shouldDisablePushNotification ? false : push_authorized,
    };

    // eslint-disable-next-line promise/catch-or-return
    dispatch(
      // eslint-disable-next-line import/no-deprecated
      updateOnboardingUser({
        // @ts-expect-error TS(2769): No overload matches this call.
        phi_auth_data: JSON.stringify(phi_auth_data),
      }),
    ).then((success: boolean) => {
      // eslint-disable-next-line promise/always-return
      if (success) {
        navigate(nextRoute);
        const phiPref = [];

        if (sms_authorized) {
          phiPref.push('text');
        }

        if (email_authorized) {
          phiPref.push('email');
        }

        if (push_authorized) {
          phiPref.push('push_notifications');
        }
        trackEvent({
          event: EVENTS.ONBOARDING__PHI_AUTHORIZATION_COMPLETED,
          params: {
            origin: ORIGIN_NAMES.ONBOARDING,
            message_preference: phiPref,
          },
        });
      }
    });
  };

  usePageViewedEvent({
    event: EVENTS.ONBOARDING__PHI_AUTHORIZATION_VIEWED,
    params: {
      is_progyny_rx_view: showProgyny,
      origin,
    },
  });

  useEffect(() => {
    dispatch(
      // eslint-disable-next-line import/no-deprecated
      updateOnboardingUser({
        onboard_state: ONBOARD_STATE_PHI_AUTHORIZATION,
      }),
    );
  }, [dispatch]);

  if (isFetching) {
    return (
      <XlPadding>
        <LoadingEmptyState />
      </XlPadding>
    );
  }

  return (
    <>
      <SecondaryPage
        headerBackgroundColor={headerBackgroundColor}
        withoutWebFramingElementHeights
        dismissIcon="chevronleft"
        onDismiss={onBack}
        NavBarCenterContent={
          <OnboardingNavBarLogo
            illustrationSrc={headerLogoUrl}
            partnershipName={partnershipName}
          />
        }
        HeaderContent={
          <OnboardingHeader
            title={title}
            subtitle={<Body center>{subTitle}</Body>}
            illustration={
              <LgPadding
                topPadding={SPACING.STATIC.NONE}
                bottomPadding={SPACING.STATIC.NONE}
                leftPadding={isLGScreenOrBigger ? SPACING.STATIC.LG : SPACING.STATIC.NONE}
                rightPadding={isLGScreenOrBigger ? SPACING.STATIC.LG : SPACING.STATIC.NONE}
              >
                <StyledSizedImage
                  center
                  source={onboardingPhiImage}
                  resizeMode="contain"
                  aspectRatio={3}
                />
              </LgPadding>
            }
          />
        }
        buttons={[
          <Button
            label="Continue"
            onPress={handleSubmit(onSubmit)}
            key="submit button"
            disabled={updateOnboardingUserLoading}
            loading={updateOnboardingUserLoading}
          />,
        ]}
        footerPlacementContext="webScreenWithoutPolicyFooter"
      >
        <Card>
          <MdPadding>
            <Column>
              <LgPadding
                topPadding={SPACING.STATIC.XS}
                bottomPadding={SPACING.STATIC.XS}
              >
                <Switch
                  testID="switch-phi"
                  accessibilityLabel="switch"
                  label="Include in all messages"
                  labelColor={COLORS.TEXT_COLORS.PRIMARY}
                  value={allPhiAuthed}
                  onValueChange={(val) => {
                    setValue('sms_authorized', val);
                    setValue('email_authorized', val);
                    setValue('push_authorized', shouldDisablePushNotification ? false : val);
                  }}
                />
              </LgPadding>
              {shouldDisablePushNotification || isFeatureDisablingLoading ? null : (
                <InputCheckbox
                  key="push_authorized"
                  name="push_authorized"
                  label="Push Notifications"
                  noHighlight={true}
                  onPress={() => {
                    pushController.field.onChange(!pushValue);
                  }}
                  value={pushController.field.value}
                />
              )}
              <InputCheckbox
                key="sms_authorized"
                name="sms_authorized"
                label="Text Messages/SMS"
                noHighlight={true}
                onPress={() => {
                  smsController.field.onChange(!smsValue);
                }}
                value={smsController.field.value}
              />
              <InputCheckbox
                key="email_authorized"
                name="email_authorized"
                label="Emails"
                noHighlight={true}
                onPress={() => {
                  emailController.field.onChange(!emailValue);
                }}
                value={emailController.field.value}
              />
            </Column>
          </MdPadding>
        </Card>
      </SecondaryPage>
    </>
  );
};

export default OnboardingPhiAuthorization;
